<template>
  <div>
    <div class="p-6 w-full shrink-0">
      <div v-if="loading" class="flex justify-center items-center" style="min-height: 400px">
        <i class="pi pi-spin pi-spinner text-4xl text-gray-500"></i>
      </div>

      <!-- Event not found or not live -->
      <div
        v-else-if="eventLoadError"
        class="flex flex-col justify-center items-center"
        style="height: calc(100vh - 250px)"
      >
        <i class="pi pi-exclamation-circle text-4xl text-gray-400 mb-4"></i>
        <p class="text-gray-600 dark:text-gray-300 text-lg">{{ eventLoadError }}</p>
      </div>

      <!-- Multiple live events — picker -->
      <div
        v-else-if="liveEventsPicker.length"
        class="flex flex-col justify-center items-center max-w-lg mx-auto"
        style="min-height: calc(100vh - 250px)"
      >
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Select a live event</h2>
        <p class="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Multiple events are live right now. Choose one to open its control panel.
        </p>
        <ul class="w-full space-y-2">
          <li v-for="event in liveEventsPicker" :key="event.id">
            <router-link
              :to="`/events/live/${event.id}`"
              class="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 hover:border-teal-500 dark:hover:border-teal-400 transition-colors"
            >
              <i class="pi pi-circle-fill text-green-500 text-xs"></i>
              <span class="font-medium text-gray-900 dark:text-white">{{ event.name }}</span>
            </router-link>
          </li>
        </ul>
      </div>

      <!-- No Active Event - Show Sleeping Cat -->
      <div
        v-else-if="!activeEvent"
        class="flex flex-col justify-center items-center"
        style="height: calc(100vh - 250px)"
      >
        <SleepingCat />
        <DancingCat />
        <div v-if="nextEvent" class="mt-1 text-center">
          <p class="text-gray-600 dark:text-gray-300 text-sm">
            Next event: <span class="font-semibold">{{ nextEvent.name }}</span>
          </p>
          <p class="text-gray-500 dark:text-gray-400 text-sm">
            {{ formatEventStartTime(nextEvent) }}
          </p>
        </div>
      </div>

      <!-- Active Event Information -->
      <div v-else class="space-y-6">
        <!-- Event Header -->
        <div
          class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
        >
          <div class="flex items-start justify-between gap-4 mb-4">
            <h1 class="text-xl font-bold text-gray-900 dark:text-white">
              {{ activeEvent.name }}
            </h1>
            <div
              class="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-sm font-medium shrink-0"
            >
              <i class="pi pi-circle-fill text-xs"></i>
              <span>Live Now</span>
            </div>
          </div>

          <!-- Voting Status -->
          <div
            class="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800"
          >
            <div class="flex items-center justify-between gap-4">
              <p class="font-semibold text-blue-900 dark:text-blue-100">
                Voting is {{ votingStatusText }}
              </p>
              <p
                v-if="votingEndTime"
                class="text-sm text-blue-700 dark:text-blue-200 shrink-0 text-right"
              >
                Ends: {{ formatDateTime(votingEndTime) }}
              </p>
            </div>
          </div>

          <PhoneOverlayStatus
            class="mt-4"
            :active="phoneOverlayActive"
            :template-name="activeOverlayName"
          />

          <!-- Setlist Set Timer -->
          <div
            v-if="isSetlistActive && currentSetDuration > 0"
            class="mt-4 p-4 rounded-lg border"
            :class="{
              'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800':
                setRemainingSeconds >
                ((setlistSettings?.defaultSongTime || 240) + (setlistSettings?.pauseTime || 30)) *
                  2,
              'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800':
                setRemainingSeconds > 0 &&
                setRemainingSeconds <=
                  ((setlistSettings?.defaultSongTime || 240) + (setlistSettings?.pauseTime || 30)) *
                    2,
              'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800':
                setRemainingSeconds <= 0
            }"
          >
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Set {{ (activeEvent.currentSetIndex || 0) + 1 }} of
                  {{ activeEvent.setlistDurations?.length || 0 }}
                  <span class="ml-2 text-xs text-gray-500">({{ currentSetDuration }} min)</span>
                </p>
                <p class="text-2xl font-bold mt-1" :class="setRemainingUrgency">
                  <i class="pi pi-stopwatch mr-1"></i>
                  {{ setRemainingSeconds > 0 ? setRemainingFormatted : "Set time over" }}
                </p>
              </div>
              <div
                class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto"
              >
                <div class="flex flex-col gap-1">
                  <div
                    class="flex items-center justify-between sm:justify-center gap-2 px-2 py-1 rounded-lg border bg-white/60 dark:bg-gray-900/40"
                    :class="
                      pinHint
                        ? 'border-amber-400 dark:border-amber-500'
                        : 'border-gray-200 dark:border-gray-700'
                    "
                    title="Songs that still fit. Pin to hold the line in place."
                  >
                    <Button
                      icon="pi pi-minus"
                      severity="secondary"
                      text
                      rounded
                      size="small"
                      :disabled="extendingSet || (effectiveSlots ?? 0) <= 0"
                      @click="adjustSetEndSongCount(-1)"
                    />
                    <span
                      class="min-w-[3rem] text-center text-sm font-semibold text-gray-800 dark:text-gray-100"
                    >
                      {{ effectiveSlots ?? "—" }}
                      <span
                        v-if="!isSetEndPinned"
                        class="block text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                      >
                        Auto
                      </span>
                    </span>
                    <Button
                      icon="pi pi-plus"
                      severity="secondary"
                      text
                      rounded
                      size="small"
                      :disabled="extendingSet"
                      :loading="extendingSet"
                      @click="adjustSetEndSongCount(1)"
                    />
                    <Button
                      :icon="isSetEndPinned ? 'pi pi-lock-open' : 'pi pi-lock'"
                      :label="isSetEndPinned ? 'Auto' : 'Pin'"
                      severity="secondary"
                      text
                      size="small"
                      :disabled="extendingSet || (!isSetEndPinned && liveSlots == null)"
                      @click="isSetEndPinned ? unpinSetEndLine() : pinSetEndLine()"
                    />
                  </div>
                  <p v-if="pinHint" class="text-xs text-amber-700 dark:text-amber-300">
                    {{ pinHint }}
                  </p>
                </div>
                <Button
                  label="Prepare Next Set"
                  :icon="isStartingExtraSet ? 'pi pi-plus-circle' : 'pi pi-forward'"
                  severity="info"
                  size="small"
                  class="w-full sm:w-auto"
                  :disabled="setRemainingSeconds > 0"
                  @click="handleStartNextSet"
                />
              </div>
            </div>
          </div>

          <!-- Prepared Set Banner (set prepared, waiting for first song) -->
          <div
            v-if="isSetPreparedAwaitingStart && activeEvent.setlistEnabled"
            class="mt-4 p-4 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
          >
            <div class="flex items-start gap-3">
              <i class="pi pi-check-circle text-blue-600 dark:text-blue-400 text-xl mt-0.5"></i>
              <div>
                <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Set {{ preparedSetNumber }} prepared
                  <span v-if="currentSetDuration > 0" class="ml-2 text-xs text-gray-500">
                    ({{ currentSetDuration }} min)
                  </span>
                </p>
                <p class="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  {{
                    currentSetDuration > 0
                      ? `${currentSetDuration} min timer will start with first song`
                      : "No timer — ready to start"
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Currently Playing Song Display -->
        <div v-if="currentlyPlayingSong && !isKaraokeLive" class="mb-6">
          <div
            class="bg-white dark:bg-gray-800 rounded-xl border-2 p-4 border-green-500 dark:border-green-600 bg-green-50/30 dark:bg-green-900/20 ring-2 ring-green-400 dark:ring-green-500"
          >
            <div class="flex flex-col sm:flex-row sm:items-center gap-3">
              <!-- Song Info -->
              <div class="flex-1 min-w-0">
                <span
                  class="text-xs uppercase tracking-widest font-semibold text-green-600 dark:text-green-400 mb-1 block animate-pulse"
                >
                  Now Playing
                </span>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white leading-snug">
                  {{ currentlyPlayingSong.title }}
                </h3>
                <p
                  v-if="currentlyPlayingSong.author"
                  class="text-gray-600 dark:text-gray-400 text-sm mt-0.5"
                >
                  {{ currentlyPlayingSong.author }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                  Performed at: {{ formatDateTime(currentlyPlayingSong.performedAt) }}
                </p>
              </div>
              <!-- Set End Button -->
              <div v-if="isStaffUser" class="shrink-0">
                <button
                  class="w-full sm:w-auto min-w-[10rem] px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  @click="handleSetEnd(currentlyPlayingSong.id)"
                >
                  <i class="pi pi-stop-circle"></i>
                  Set end
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Up Next Song Display -->
        <div v-if="upNextSong && !isKaraokeLive" class="mb-6">
          <div
            class="bg-white dark:bg-gray-800 rounded-xl border-4 border-double p-4 border-amber-400 dark:border-amber-500 bg-amber-50/30 dark:bg-amber-900/20 ring-2 ring-amber-300 dark:ring-amber-500"
          >
            <div class="flex flex-col sm:flex-row sm:items-center gap-3">
              <!-- Song Info -->
              <div class="flex-1 min-w-0">
                <span
                  class="text-xs uppercase tracking-widest font-semibold text-amber-600 dark:text-amber-400 mb-1 block"
                >
                  Up Next
                </span>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white leading-snug">
                  {{ upNextSong.title }}
                </h3>
                <p v-if="upNextSong.author" class="text-gray-600 dark:text-gray-400 text-sm mt-0.5">
                  {{ upNextSong.author }}
                </p>
              </div>
              <!-- Unlock Button -->
              <div v-if="isStaffUser" class="shrink-0">
                <button
                  :disabled="clearingUpNext"
                  class="w-full sm:w-auto min-w-[10rem] px-4 py-2 bg-gray-500 hover:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  @click="handleClearUpNext"
                >
                  <i :class="clearingUpNext ? 'pi pi-spin pi-spinner' : 'pi pi-unlock'"></i>
                  Unlock
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Admin Controls -->
        <div v-if="isStaffUser" class="mb-6 space-y-4">
          <template v-if="!isKaraokeLive">
            <div v-if="showStartSetButton">
              <Button
                :label="startSetButtonLabel"
                :icon="startSetButtonIcon"
                :severity="startSetButtonSeverity"
                size="large"
                class="w-full py-4 text-lg font-semibold"
                :loading="playingNextSong"
                @click="handlePlayNextSong"
              />
              <p
                v-if="startSetHelperText"
                class="mt-2 text-sm text-center"
                :class="
                  isSetPreparedAwaitingStart
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-amber-600 dark:text-amber-400'
                "
              >
                {{ startSetHelperText }}
              </p>
            </div>
            <!-- Play without queueing (only shows when autoQueueNext is enabled and there's a current song) -->
            <Button
              v-if="activeEvent.autoQueueNext && hasCurrentSong"
              label="Play next song w/o queue"
              icon="pi pi-step-forward"
              severity="warn"
              size="large"
              class="w-full py-4 text-lg font-semibold !bg-amber-500 !text-white !border-amber-500 hover:!bg-amber-600 hover:!border-amber-600 dark:!bg-amber-600 dark:!border-amber-600 dark:hover:!bg-amber-700 dark:hover:!border-amber-700"
              :loading="playingNextSongNoQueue"
              @click="handlePlayNextSongNoQueue"
            />
            <Button
              :label="activeEvent.isVotingDisabled ? 'Resume Voting' : 'Stop All Voting'"
              :icon="activeEvent.isVotingDisabled ? 'pi pi-play' : 'pi pi-stop-circle'"
              :severity="activeEvent.isVotingDisabled ? 'success' : 'danger'"
              size="large"
              class="live-quiet-danger w-full py-4 text-lg font-semibold"
              :loading="togglingVoting"
              @click="toggleVoting"
            />
          </template>

          <!-- Karaoke QR Scanner Button (staff only) -->
          <Button
            v-if="isKaraokeLive && isStaffUser && activeEvent?.signUpMode === 'qr'"
            label="Scan QR Code"
            icon="pi pi-qrcode"
            severity="info"
            size="large"
            class="w-full py-4 text-lg font-semibold"
            @click="showQrScanner = true"
          />

          <!-- End Event -->
          <Button
            v-if="isStaffUser"
            label="End Event"
            icon="pi pi-times-circle"
            severity="danger"
            outlined
            size="large"
            class="live-quiet-danger w-full py-4 text-lg font-semibold"
            :loading="endingEvent"
            @click="confirmEndEvent"
          />

          <!-- Overlay on phones -->
          <div
            v-if="isStaffUser"
            class="rounded-lg border p-4 space-y-3"
            :class="
              phoneOverlayActive
                ? 'border-amber-300 bg-amber-50/70 dark:border-amber-700 dark:bg-amber-900/20'
                : 'border-gray-200 dark:border-gray-700'
            "
          >
            <div class="flex items-center justify-between gap-3">
              <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">
                Show/hide Overlay Screen
              </p>
              <span
                data-testid="overlay-control-status"
                class="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                :class="
                  phoneOverlayActive
                    ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                "
              >
                <i
                  :class="phoneOverlayActive ? 'pi pi-eye' : 'pi pi-eye-slash'"
                  class="text-[10px]"
                  aria-hidden="true"
                ></i>
                {{ phoneOverlayActive ? "On phones" : "Hidden" }}
              </span>
            </div>
            <Select
              v-model="selectedOverlayId"
              :options="overlayTemplates"
              option-label="name"
              option-value="id"
              placeholder="Choose a template"
              class="w-full"
            />
            <div class="flex gap-2">
              <Button
                label="Show overlay"
                icon="pi pi-eye"
                class="flex-1"
                data-testid="show-phone-overlay"
                :disabled="!selectedOverlayId"
                :loading="overlaySaving"
                @click="showPhoneOverlay"
              />
              <Button
                label="Hide overlay"
                icon="pi pi-eye-slash"
                severity="secondary"
                outlined
                class="flex-1"
                data-testid="hide-phone-overlay"
                :disabled="!phoneOverlayActive"
                :loading="overlaySaving"
                @click="hidePhoneOverlay"
              />
            </div>
          </div>
        </div>

        <!-- Tabs and Song Content -->
        <div v-if="songs.length > 0">
          <!-- View Toggle Tabs -->
          <div class="mb-6 flex gap-2 border-b border-gray-200 dark:border-gray-700">
            <button
              :class="[
                'px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px',
                activeView === 'vote'
                  ? 'text-teal-600 dark:text-teal-400 border-teal-600 dark:border-teal-400'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
              ]"
              @click="activeView = 'vote'"
            >
              <i class="pi pi-heart mr-2"></i>
              Vote
            </button>
            <button
              :class="[
                'px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px',
                activeView === 'results'
                  ? 'text-teal-600 dark:text-teal-400 border-teal-600 dark:border-teal-400'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
              ]"
              @click="activeView = 'results'"
            >
              <i class="pi pi-chart-bar mr-2"></i>
              Results
            </button>
            <button
              :class="[
                'px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px',
                activeView === 'performed'
                  ? 'text-teal-600 dark:text-teal-400 border-teal-600 dark:border-teal-400'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
              ]"
              @click="activeView = 'performed'"
            >
              <i class="pi pi-check-circle mr-2"></i>
              Performed
            </button>
          </div>

          <!-- Voting View -->
          <div v-if="activeView === 'vote'">
            <!-- Filters Section -->
            <div
              v-if="songs.length > 0"
              class="mb-8 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm"
            >
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Search/Title Filter -->
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >Search</label
                  >
                  <IconField icon-position="left" class="w-full">
                    <InputIcon class="pi pi-search text-gray-400 dark:text-gray-500" />
                    <InputText
                      v-model="filters.title"
                      placeholder="Title or artist..."
                      class="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </IconField>
                </div>

                <!-- Category Filter -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >Category</label
                  >
                  <Select
                    v-model="filters.category"
                    :options="categoryOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="All categories"
                    class="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm"
                    :show-clear="true"
                  />
                </div>

                <!-- Sort by Votes -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >Sort</label
                  >
                  <Select
                    v-model="filters.sortBy"
                    :options="sortOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="Select..."
                    class="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm"
                  />
                </div>
              </div>
            </div>

            <div v-if="filteredSongs.length === 0" class="text-center py-16">
              <div
                class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <i class="pi pi-filter text-3xl text-gray-400"></i>
              </div>
              <p class="text-gray-600 dark:text-gray-400 text-lg mb-4">
                No songs match your filters
              </p>
              <button
                class="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium transition-colors"
                @click="clearFilters"
              >
                Clear filters
              </button>
            </div>

            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <SongCard
                v-for="song in filteredSongs"
                :key="song.id"
                :song="song"
                :event-id="String(activeEvent.id)"
                :vote-count="getVoteCount(song.id)"
                :has-voted="isKaraokeLive ? false : hasUserVoted(song.id)"
                :is-performed="song.isPerformed"
                :voting-enabled="true"
                :show-mark-performed="false"
                :show-vote-count="!isKaraokeLive"
                :show-vote-button="true"
                :queue-action-label="isKaraokeLive ? 'Add to queue' : ''"
                @vote="handleVote"
              />
            </div>
          </div>

          <!-- Results View -->
          <div v-else-if="activeView === 'results'">
            <div v-if="isKaraokeLive" class="space-y-3">
              <div
                v-if="!karaokeNowPlaying && karaokeQueueEntries.length === 0"
                class="text-center py-16"
              >
                <p class="text-gray-600 dark:text-gray-400 text-lg">
                  No songs in the karaoke queue yet
                </p>
              </div>
              <KaraokeQueueRow
                v-if="karaokeNowPlaying"
                :entry="karaokeNowPlaying"
                :is-now-playing="true"
                :show-admin-actions="isStaffUser"
                @mark-performed="handleKaraokeMarkPerformed"
              />
              <TransitionGroup name="karaoke-queue" tag="div" class="space-y-3">
                <div
                  v-for="(entry, index) in karaokeQueueEntries"
                  :key="entry.voteId"
                  class="karaoke-queue-item"
                >
                  <KaraokeQueueRow
                    :entry="entry"
                    :queue-number="index + 1"
                    :show-admin-actions="isStaffUser"
                    :disable-start="!!karaokeNowPlaying"
                    :can-move-up="index > 0"
                    :can-move-down="index < karaokeQueueEntries.length - 1"
                    @start-song="handleKaraokeStartSong"
                    @move-up="handleKaraokeMoveUp"
                    @move-down="handleKaraokeMoveDown"
                  />
                </div>
              </TransitionGroup>
            </div>

            <template v-else>
              <div v-if="sortedResults.length === 0 && !upNextSong" class="text-center py-16">
                <div
                  class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <i class="pi pi-chart-bar text-3xl text-gray-400"></i>
                </div>
                <p class="text-gray-600 dark:text-gray-400 text-lg">No votes have been cast yet</p>
              </div>

              <div v-else class="space-y-3">
                <!-- Nothing left in this set, including a reserved Up Next. -->
                <SetEndDivider v-if="setEndCoversUpNext" :label="setEndLabel" />
                <!-- Up Next Song (shown as song 0 at the top) -->
                <div
                  v-if="upNextSong"
                  class="bg-white dark:bg-gray-800 rounded-xl border-2 p-6 hover:shadow-md transition-all border-amber-400 dark:border-amber-500 bg-amber-50/30 dark:bg-amber-900/20 ring-2 ring-amber-300 dark:ring-amber-500"
                >
                  <div class="results-card-layout">
                    <!-- Up Next Icon -->
                    <div class="results-card-rank">
                      <div
                        class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white bg-amber-500 dark:bg-amber-600"
                        title="Up Next"
                      >
                        <i class="pi pi-forward text-2xl"></i>
                      </div>
                    </div>

                    <!-- Song Info -->
                    <div class="results-card-info min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                          {{ upNextSong.title }}
                        </h3>
                      </div>
                      <p v-if="upNextSong.author" class="text-gray-600 dark:text-gray-400 text-sm">
                        {{ upNextSong.author }}
                      </p>
                    </div>

                    <!-- Up Next Badge -->
                    <div class="results-card-votes text-center">
                      <span
                        class="inline-block px-4 py-2 text-sm bg-amber-500 text-white rounded-full font-bold uppercase tracking-wide"
                      >
                        UP NEXT
                      </span>
                    </div>

                    <!-- Mark Performed Button -->
                    <div class="results-card-button">
                      <button
                        v-if="isStaffUser && !getIsPerformed(upNextSong.id)"
                        class="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2 w-full sm:w-auto justify-center"
                        @click="handleMarkPerformed(upNextSong.id)"
                      >
                        <i class="pi pi-check-circle"></i>
                        Mark Performed
                      </button>
                    </div>
                  </div>
                </div>

                <template v-for="(song, index) in sortedResultsFiltered" :key="song.id">
                  <SetEndDivider
                    v-if="index === setEndLineIndex && !setEndCoversUpNext"
                    :label="setEndLabel"
                  />
                  <div
                    class="bg-white dark:bg-gray-800 rounded-xl border-2 p-6 hover:shadow-md transition-all"
                    :class="{
                      'border-teal-400 dark:border-teal-500 bg-teal-50/20 dark:bg-teal-900/10':
                        index === 0,
                      'border-teal-500 dark:border-teal-600 bg-teal-50/15 dark:bg-teal-900/8':
                        index === 1,
                      'border-teal-600 dark:border-teal-700 bg-teal-50/10 dark:bg-teal-900/6':
                        index === 2,
                      'border-teal-700 dark:border-teal-800 bg-teal-50/5 dark:bg-teal-900/4':
                        index === 3,
                      'border-teal-800 dark:border-teal-900': index >= 4
                    }"
                  >
                    <div class="results-card-layout">
                      <!-- Rank Badge -->
                      <div class="results-card-rank">
                        <div
                          class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white"
                          :class="{
                            'bg-teal-400 dark:bg-teal-500': index === 0,
                            'bg-teal-500 dark:bg-teal-600': index === 1,
                            'bg-teal-600 dark:bg-teal-700': index === 2,
                            'bg-teal-700 dark:bg-teal-800': index === 3,
                            'bg-teal-800 dark:bg-teal-900': index >= 4
                          }"
                          data-index="index"
                        >
                          {{ index + 1 }}
                        </div>
                      </div>

                      <!-- Song Info -->
                      <div class="results-card-info min-w-0">
                        <div class="flex items-center gap-2 mb-1">
                          <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                            {{ song.title }}
                          </h3>
                          <i
                            v-if="hasUserVoted(song.id)"
                            class="pi pi-check-circle text-teal-600 dark:text-teal-400"
                            title="You voted for this song"
                          ></i>
                        </div>
                        <p v-if="song.author" class="text-gray-600 dark:text-gray-400 text-sm">
                          {{ song.author }}
                        </p>
                      </div>

                      <div v-if="song.pinned" class="results-card-votes flex justify-center">
                        <i
                          class="pi pi-thumbtack shrink-0 !text-2xl text-amber-600 dark:text-amber-300"
                          title="Pinned to the top of the playlist"
                          data-testid="pinned-song"
                        ></i>
                      </div>
                      <!-- Vote Count -->
                      <div v-else class="results-card-votes text-center">
                        <div class="flex items-center justify-center gap-2 mb-1">
                          <i class="pi pi-heart text-teal-500 text-xl"></i>
                          <span class="text-3xl font-bold text-gray-900 dark:text-white">
                            {{ getVoteCount(song.id) }}
                          </span>
                        </div>
                        <span class="text-sm text-gray-500 dark:text-gray-400">
                          {{ getVoteCount(song.id) === 1 ? "vote" : "votes" }}
                        </span>
                      </div>

                      <!-- Mark Performed Button -->
                      <div class="results-card-button">
                        <button
                          v-if="isStaffUser && !getIsPerformed(song.id)"
                          class="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2 w-full sm:w-auto justify-center"
                          @click="handleMarkPerformed(song.id)"
                        >
                          <i class="pi pi-check-circle"></i>
                          Mark Performed
                        </button>
                        <div
                          v-else-if="getIsPerformed(song.id)"
                          class="px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg text-sm font-medium flex items-center gap-2"
                        >
                          <i class="pi pi-check-circle"></i>
                          Performed
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <SetEndDivider
                  v-if="setEndLineIndex >= sortedResultsFiltered.length && !setEndCoversUpNext"
                  :label="setEndLabel"
                />
              </div>
            </template>
          </div>

          <!-- Performed Songs View -->
          <div v-else-if="activeView === 'performed'">
            <div v-if="performedSongs.length === 0" class="text-center py-16">
              <div
                class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <i class="pi pi-check-circle text-3xl text-gray-400"></i>
              </div>
              <p class="text-gray-600 dark:text-gray-400 text-lg">
                No songs have been marked as performed yet
              </p>
            </div>

            <div v-else class="space-y-3">
              <div
                v-for="(song, index) in performedSongs"
                :key="`${song.id}-${song.performedAt || index}`"
                class="bg-white dark:bg-gray-800 rounded-xl border-2 p-6 hover:shadow-md transition-all"
                :class="{
                  'current-song-card border-green-500 dark:border-green-600 bg-green-50/30 dark:bg-green-900/20 ring-2 ring-green-400 dark:ring-green-500':
                    index === 0 && !song.endedAt,
                  'border-emerald-700 dark:border-emerald-800 bg-emerald-50/10 dark:bg-emerald-900/10':
                    index > 0 || song.endedAt
                }"
              >
                <div class="results-card-layout">
                  <!-- Performance Order Badge / Currently Playing Icon -->
                  <div class="results-card-rank">
                    <div
                      v-if="index === 0 && !song.endedAt"
                      class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white animate-pulse"
                      :class="
                        isKaraokeLive
                          ? 'bg-amber-500 dark:bg-amber-600'
                          : 'bg-green-500 dark:bg-green-600'
                      "
                      title="Currently being performed"
                    >
                      <i
                        :class="
                          isKaraokeLive ? 'pi pi-microphone text-2xl' : 'pi pi-volume-up text-2xl'
                        "
                      ></i>
                    </div>
                    <div
                      v-else
                      class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white bg-emerald-700 dark:bg-emerald-800"
                    >
                      {{ index + 1 }}
                    </div>
                  </div>

                  <!-- Song Info -->
                  <div class="results-card-info min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                        {{ song.title }}
                      </h3>
                      <i
                        v-if="song.endedAt || index > 0"
                        class="pi pi-check-circle text-emerald-700 dark:text-emerald-500"
                        title="Performed"
                      ></i>
                    </div>
                    <p v-if="song.author" class="text-gray-600 dark:text-gray-400 text-sm">
                      {{ song.author }}
                    </p>
                    <p
                      v-if="song.performerName"
                      class="text-base font-oswald tracking-wide text-purple-600 dark:text-purple-300 mt-0.5 flex items-center gap-1"
                    >
                      <i class="pi pi-user" style="font-size: 12px"></i>
                      {{ song.performerName }}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Performed at: {{ formatDateTime(song.performedAt) }}
                      <span v-if="song.endedAt" class="ml-2">
                        • Ended at: {{ formatDateTime(song.endedAt) }}
                      </span>
                    </p>
                  </div>

                  <!-- Vote Count -->
                  <div v-if="!isKaraokeLive" class="results-card-votes text-center">
                    <div class="flex items-center justify-center gap-2 mb-1">
                      <i class="pi pi-heart text-teal-500 text-xl"></i>
                      <span class="text-3xl font-bold text-gray-900 dark:text-white">
                        {{ getVoteCount(song.id) }}
                      </span>
                    </div>
                    <span class="text-sm text-gray-500 dark:text-gray-400">
                      {{ getVoteCount(song.id) === 1 ? "vote" : "votes" }}
                    </span>
                  </div>

                  <!-- Set End Button (for currently playing) / Status badge (for completed) -->
                  <div v-if="!isKaraokeLive" class="results-card-button">
                    <button
                      v-if="index === 0 && !song.endedAt && isStaffUser"
                      class="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2 w-full sm:w-auto justify-center"
                      @click="handleSetEnd(song.id)"
                    >
                      <i class="pi pi-stop-circle"></i>
                      Set end
                    </button>
                    <div
                      v-else
                      class="px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                      <i class="pi pi-check-circle"></i>
                      Performed
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="live-page-bottom-spacer" aria-hidden="true" />
      </div>
    </div>

    <!-- Extra Set Modal: shown when admin starts a set beyond predefined list -->
    <Dialog
      v-model:visible="showExtraSetModal"
      :draggable="false"
      modal
      header="Start Extra Set"
      pt:root:class="!border-0 !bg-transparent"
      :closable="true"
      @hide="pendingSetAutoPlay = false"
    >
      <div class="space-y-6">
        <div
          class="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg"
        >
          <p class="text-blue-800 dark:text-blue-200 font-medium flex items-center gap-2">
            <i class="pi pi-info-circle"></i>
            All predefined sets have been used.
          </p>
          <p class="text-sm text-blue-700 dark:text-blue-300 mt-1">
            You can define a duration for this extra set, or start it without a timer.
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Set duration (minutes) — leave empty for no timer
          </label>
          <InputNumber
            v-model="extraSetDurationMin"
            :min="1"
            :max="300"
            placeholder=""
            class="w-full"
            input-class="w-full"
            @keyup.enter="handleExtraSetConfirm(true)"
          />
        </div>
      </div>

      <template #footer>
        <div class="flex justify-between gap-2">
          <Button
            label="Start with duration"
            severity="info"
            icon="pi pi-forward"
            :disabled="!extraSetDurationMin || extraSetDurationMin <= 0"
            @click="handleExtraSetConfirm(true)"
          />
          <Button
            label="Start without timer"
            severity="secondary"
            icon="pi pi-play"
            @click="handleExtraSetConfirm(false)"
          />
          <Button label="Cancel" severity="secondary" outlined @click="handleExtraSetModalCancel" />
        </div>
      </template>
    </Dialog>

    <KaraokePerformerModal
      v-model:visible="showKaraokePerformerModal"
      :song-title="pendingKaraokeSong?.title || ''"
      @confirm="handleKaraokePerformerConfirm"
    />

    <!-- Karaoke QR Scanner -->
    <QRCodeScanner
      v-if="isKaraokeLive && activeEvent?.signUpMode === 'qr'"
      v-model:visible="showQrScanner"
      :event-id="activeEvent.id"
      @accepted="handleQRAccepted"
      @rejected="handleQRRejected"
      @update:visible="handleQRModalClose"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useVotesStore } from "@/stores/votes";
import { useSocketStore } from "@/stores/socket";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { formatDateTime, formatDateWithWeekday } from "@/utils/dateFormat";
import {
  computeRemainingSetTime,
  formatCountdown,
  buildSongLengthMap,
  resolveSetEndIndex,
  setLeftOpenAfterSongEnded
} from "@/utils/setlistCalculator";
import { useSetEndSlots } from "@/composables/useSetEndSlots";
import { filterSongsForEvent } from "@/utils/eventSongFilter";
import { includeVoteInRankedPlaylist } from "@/utils/rankedPlaylist";
import { postEventOverlay } from "@/utils/eventOverlayApi";
import { noteLiveEventOverlay } from "@/composables/useLiveEvents";
import { isEventLive, getLiveEvents } from "@/utils/eventStatus";
import { isPrevoting, isKaraokeEvent } from "@/constants/eventTypes";
import KaraokePerformerModal from "@/components/admin/KaraokePerformerModal.vue";
import KaraokeQueueRow from "@/components/events/KaraokeQueueRow.vue";
import QRCodeScanner from "@/components/QRCodeScanner.vue";
import api from "@/services/api";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import InputNumber from "primevue/inputnumber";
import SleepingCat from "@/components/SleepingCat.vue";
import DancingCat from "@/components/DancingCat.vue";
import SongCard from "@/components/events/SongCard.vue";
import PhoneOverlayStatus from "@/components/events/PhoneOverlayStatus.vue";
import SetEndDivider from "@/components/SetEndDivider.vue";

const authStore = useAuthStore();
const isStaffUser = computed(() => authStore.isStaff);
const votesStore = useVotesStore();
const socketStore = useSocketStore();
const toast = useToast();
const confirm = useConfirm();
const route = useRoute();
const router = useRouter();
const activeEvent = ref(null);
const nextEvent = ref(null);
const liveEventsPicker = ref([]);
const eventLoadError = ref(null);
const songs = ref([]);
const loading = ref(true);
let joinedEventId = null;
const activeView = ref("vote"); // 'vote', 'results', or 'performed'
const togglingVoting = ref(false);
const endingEvent = ref(false);
const overlayTemplates = ref([]);
const selectedOverlayId = ref(null);
const overlaySaving = ref(false);
const phoneOverlayActive = computed(() => Boolean(activeEvent.value?.activeOverlayTemplateId));
const activeOverlayName = computed(() => {
  const id = activeEvent.value?.activeOverlayTemplateId;
  if (!id) return "";
  return overlayTemplates.value.find((template) => template.id === id)?.name || "";
});
const playingNextSong = ref(false);
const playingNextSongNoQueue = ref(false);
const clearingUpNext = ref(false);
const showKaraokePerformerModal = ref(false);
const pendingKaraokeSong = ref(null);

// QR Scanner state
const showQrScanner = ref(false);

// ── Setlist timer state ──────────────────────────────────────────────────────
const setlistSettings = ref(null); // { pauseTime, defaultSongTime }
const setRemainingSeconds = ref(-1);
let setTimerInterval = null;
// null = live (timer-driven). A number pins the line until the admin returns it to Auto.
const setEndSongCount = ref(null);
// const showPlayNextConfirmModal = ref(false);
// const playNextConfirmResolve = ref(null);

// ── Extra set modal (when admin starts a set beyond predefined list) ──────────
const showExtraSetModal = ref(false);
const extraSetDurationMin = ref(null); // minutes typed by admin (null = untimed)
const extendingSet = ref(false);
const pendingSetAutoPlay = ref(false); // true when prepare flow was initiated from "Start set"

const filters = ref({
  title: "",
  category: null,
  sortBy: null
});

const votingStartTime = computed(() => {
  if (!activeEvent.value?.votingStartTime) return null;
  return new Date(activeEvent.value.votingStartTime);
});

const votingEndTime = computed(() => {
  if (!activeEvent.value?.votingEndTime) return null;
  return new Date(activeEvent.value.votingEndTime);
});

const votingStatusText = computed(() => {
  if (!activeEvent.value) return "Unknown";

  const now = new Date();
  const start = votingStartTime.value;
  const end = votingEndTime.value;

  if (!start) return "Not started";
  if (now < start) return "Not started";
  if (end && now > end) return "Ended";
  return "Open";
});

const getVoteCount = (songId) => {
  if (!activeEvent.value) return 0;
  // Access the computed property correctly - it returns a function
  const eventVotes = votesStore.getEventVotes(activeEvent.value.id);
  const songVote = eventVotes.find((vote) => vote.songId === songId || vote.song?.id === songId);
  return songVote ? songVote.totalVotes : 0;
};

const getFirstVotedAt = (songId) => {
  if (!activeEvent.value) return null;
  const eventVotes = votesStore.getEventVotes(activeEvent.value.id);
  const songVote = eventVotes.find((vote) => vote.songId === songId || vote.song?.id === songId);
  return songVote?.firstVotedAt || null;
};

const hasUserVoted = (songId) => {
  if (!activeEvent.value) return false;
  return votesStore.hasUserVotedForSong(activeEvent.value.id, songId);
};

// Get performed status for a song from votes store
const getIsPerformed = (songId) => {
  if (!activeEvent.value) return false;
  const eventVotes = votesStore.getEventVotes(activeEvent.value.id);
  const songVote = eventVotes.find((vote) => vote.song?.id === songId || vote.songId === songId);
  return songVote ? songVote.isPerformed : false;
};

// Results view: unperformed songs in backend order. Pins stay first even with no votes.
const sortedResults = computed(() => {
  if (!activeEvent.value) return [];

  // Get the pre-sorted results from the votes store (sorted by backend)
  const eventVotes = votesStore.getEventVotes(activeEvent.value.id);

  // Pinned songs stay on the playlist with zero votes. Disabled songs are
  // already dropped by the backend; this is a safety net for a stale cache.
  const filtered = eventVotes.filter((vote) => includeVoteInRankedPlaylist(vote, songs.value));

  // Map to include song details from our local songs array for additional info if needed
  return filtered.map((vote) => {
    const songId = vote.songId || vote.song?.id;
    const localSong = songs.value.find((s) => s.id === songId);
    return {
      id: songId,
      title: vote.song?.title || localSong?.title || "Unknown",
      author: vote.song?.author || localSong?.author || "",
      originalArtist: vote.song?.originalArtist || localSong?.originalArtist || "",
      genre: vote.song?.genre || localSong?.genre || "",
      category: vote.song?.category || localSong?.category || "",
      voteCount: vote.totalVotes,
      pinned: vote.pinned === true,
      isPerformed: vote.isPerformed,
      firstVotedAt: vote.firstVotedAt,
      performedAt: vote.performedAt,
      endedAt: vote.endedAt,
      songLength: vote.song?.songLength || localSong?.songLength || null
    };
  });
});

// Results filtered to exclude the up-next song (shown separately at position 0)
const sortedResultsFiltered = computed(() => {
  if (!upNextSong.value) return sortedResults.value;
  return sortedResults.value.filter((song) => song.id !== upNextSong.value.id);
});

// ── Setlist "Set End" line computation ──────────────────────────────────────
const isSetlistActive = computed(() => {
  const ev = activeEvent.value;
  return ev && ev.setlistEnabled && ev.currentSetStartedAt && setlistSettings.value;
});

const currentSetDuration = computed(() => {
  const ev = activeEvent.value;
  if (!ev?.setlistEnabled) return 0;
  const durations = Array.isArray(ev.setlistDurations) ? ev.setlistDurations : [];
  const idx = ev.currentSetIndex || 0;
  return idx < durations.length ? durations[idx] : 0;
});

const consumeSetEndSlot = () => {
  if (setEndSongCount.value == null) return;
  setEndSongCount.value = Math.max(0, setEndSongCount.value - 1);
  if (activeEvent.value) {
    activeEvent.value = {
      ...activeEvent.value,
      setEndSongCount: setEndSongCount.value
    };
  }
};

const hydrateSetEndSongCount = (count) => {
  if (count === undefined) return;
  setEndSongCount.value = count;
};

const setRemainingFormatted = computed(() => {
  if (setRemainingSeconds.value === -1) return null; // -1 means no active set
  return formatCountdown(setRemainingSeconds.value);
});

const setRemainingUrgency = computed(() => {
  const secs = setRemainingSeconds.value;
  if (secs < 0) return "";
  if (secs <= 0) return "text-red-600 dark:text-red-400";
  const oneSongCost =
    (setlistSettings.value?.defaultSongTime || 240) + (setlistSettings.value?.pauseTime || 30);
  if (secs <= oneSongCost) return "text-red-600 dark:text-red-400";
  if (secs <= oneSongCost * 2) return "text-amber-600 dark:text-amber-400";
  return "text-green-600 dark:text-green-400";
});

const categoryOptions = computed(() => {
  const categories = new Set();
  songs.value.forEach((song) => {
    if (song.categories && Array.isArray(song.categories)) {
      song.categories.forEach((cat) => categories.add(cat));
    } else if (song.category) {
      categories.add(song.category);
    }
  });
  return Array.from(categories)
    .sort()
    .map((c) => ({ label: c, value: c }));
});

const sortOptions = computed(() => [
  { label: "Most votes", value: "votes_desc" },
  { label: "Least votes", value: "votes_asc" },
  { label: "Title A-Z", value: "title_asc" },
  { label: "Title Z-A", value: "title_desc" }
]);

const getFirstVoteSort = (a, b) => {
  const aFirst = a.firstVotedAt ? new Date(a.firstVotedAt).getTime() : Infinity;
  const bFirst = b.firstVotedAt ? new Date(b.firstVotedAt).getTime() : Infinity;
  return aFirst - bFirst;
};

const filteredSongs = computed(() => {
  let result = [...songs.value];

  if (filters.value.title) {
    const query = filters.value.title.toLowerCase();
    result = result.filter(
      (song) =>
        song.title?.toLowerCase().includes(query) || song.author?.toLowerCase().includes(query)
    );
  }

  if (filters.value.category) {
    result = result.filter((song) => {
      if (song.categories && Array.isArray(song.categories)) {
        return song.categories.includes(filters.value.category);
      }
      return song.category === filters.value.category;
    });
  }

  result = result.map((song) => ({
    ...song,
    isPerformed: getIsPerformed(song.id),
    firstVotedAt: getFirstVotedAt(song.id)
  }));

  if (filters.value.sortBy) {
    result.sort((a, b) => {
      const aVotes = getVoteCount(a.id);
      const bVotes = getVoteCount(b.id);

      switch (filters.value.sortBy) {
        case "votes_desc":
          if (bVotes !== aVotes) return bVotes - aVotes;
          return getFirstVoteSort(a, b);
        case "votes_asc":
          if (aVotes !== bVotes) return aVotes - bVotes;
          return getFirstVoteSort(a, b);
        case "title_asc":
          return (a.title || "").localeCompare(b.title || "");
        case "title_desc":
          return (b.title || "").localeCompare(a.title || "");
        default:
          return 0;
      }
    });
  }

  return result;
});

const clearFilters = () => {
  filters.value = {
    title: "",
    category: null,
    sortBy: null
  };
};

// Get the currently playing song (first song in performedSongs without endedAt)
const currentlyPlayingSong = computed(() => {
  if (performedSongs.value.length === 0) return null;
  const firstSong = performedSongs.value[0];
  return firstSong && !firstSong.endedAt ? firstSong : null;
});

// Check if there's a currently being performed song (has performedAt but no endedAt)
const hasCurrentSong = computed(() => {
  return currentlyPlayingSong.value !== null;
});

// Get the up-next song (locked, will be performed next)
const upNextSong = computed(() => {
  if (!activeEvent.value) return null;
  const upNextId = votesStore.getUpNextSongId(activeEvent.value.id);
  if (!upNextId) return null;
  // Find song details from the local songs array
  const song = songs.value.find((s) => s.id === upNextId);
  if (!song) return null;
  return {
    ...song,
    voteCount: getVoteCount(upNextId)
  };
});

// Performed songs view: songs that have been performed, sorted by performedAt (descending - latest first)
// Songs without endedAt are considered "currently being performed" and appear first
const performedSongs = computed(() => {
  if (!activeEvent.value) return [];

  if (isKaraokeLive.value) {
    const playing = votesStore.getNowPlaying(activeEvent.value.id);
    const history = votesStore.getPerformedHistory(activeEvent.value.id);
    const mapEntry = (entry, endedAt) => ({
      id: entry.songId,
      performedSongId: entry.performedSongId,
      title: entry.song?.title || "Unknown",
      author: entry.song?.author || "",
      performerName: entry.performerName || "",
      performedAt: entry.performedAt,
      endedAt: endedAt ?? entry.endedAt ?? null,
      songLength: entry.song?.songLength || null,
      voteCount: 0
    });

    const items = [];
    if (playing) {
      items.push(mapEntry(playing, null));
    }
    history.forEach((entry) => items.push(mapEntry(entry, entry.endedAt)));
    return items;
  }

  // Get the results from the votes store
  const eventVotes = votesStore.getEventVotes(activeEvent.value.id);

  // Filter: only songs that have been performed
  const filtered = eventVotes.filter((vote) => {
    return vote.isPerformed && vote.performedAt;
  });

  // Map to include song details
  const mapped = filtered.map((vote) => {
    const songId = vote.songId || vote.song?.id;
    const localSong = songs.value.find((s) => s.id === songId);
    return {
      id: songId,
      title: vote.song?.title || localSong?.title || "Unknown",
      author: vote.song?.author || localSong?.author || "",
      originalArtist: vote.song?.originalArtist || localSong?.originalArtist || "",
      genre: vote.song?.genre || localSong?.genre || "",
      category: vote.song?.category || localSong?.category || "",
      voteCount: vote.totalVotes,
      isPerformed: vote.isPerformed,
      firstVotedAt: vote.firstVotedAt,
      performedAt: vote.performedAt,
      endedAt: vote.endedAt,
      songLength: vote.song?.songLength || localSong?.songLength || null
    };
  });

  return mapped.sort((a, b) => {
    // First, prioritize songs without endedAt (currently being performed)
    if (!a.endedAt && b.endedAt) return -1;
    if (a.endedAt && !b.endedAt) return 1;

    // Then sort by performedAt timestamp (descending - latest first)
    return new Date(b.performedAt) - new Date(a.performedAt);
  });
});

const isPrevotingEvent = computed(() => isPrevoting(activeEvent.value?.eventType));
const isKaraokeLive = computed(() => isKaraokeEvent(activeEvent.value));

const karaokeNowPlaying = computed(() => {
  if (!activeEvent.value) return null;
  return votesStore.getNowPlaying(activeEvent.value.id);
});

const karaokeQueueEntries = computed(() => {
  if (!activeEvent.value) return [];
  return votesStore.getQueueEntries(activeEvent.value.id);
});

const setEndLiveSource = computed(() => {
  const playing = currentlyPlayingSong.value;
  return {
    setDurationMinutes: isSetlistActive.value ? currentSetDuration.value : 0,
    setStartedAt: activeEvent.value?.currentSetStartedAt ?? null,
    rankedSongs: sortedResultsFiltered.value,
    upNextSong: upNextSong.value,
    currentSong: playing,
    currentSongStartedAt: playing?.performedAt ?? null,
    pauseTimeSec: setlistSettings.value?.pauseTime || 30,
    defaultSongTimeSec: setlistSettings.value?.defaultSongTime || 240,
    songLengthMap: buildSongLengthMap([
      ...sortedResultsFiltered.value,
      ...(upNextSong.value ? [upNextSong.value] : []),
      ...(playing ? [playing] : [])
    ])
  };
});

const {
  liveSlots,
  liveStatus,
  start: startSetEndSlots,
  stop: stopSetEndSlots
} = useSetEndSlots(setEndLiveSource, { intervalMs: 1000 });

const isSetEndPinned = computed(() => setEndSongCount.value != null);

const effectiveSlots = computed(() =>
  isSetEndPinned.value ? setEndSongCount.value : liveSlots.value
);

const effectiveStatus = computed(() => {
  if (!isSetEndPinned.value) return liveStatus.value;
  return setEndSongCount.value === 0 ? "expired" : "partial";
});

// Shown while the line is still live and only a couple of songs still fit
// beyond the one already playing.
const pinHint = computed(() => {
  if (isSetEndPinned.value) return "";
  const slots = liveSlots.value;
  if (slots == null || slots > 2) return "";
  if (liveStatus.value !== "partial" && liveStatus.value !== "expired") return "";
  if (slots === 0) return "No songs left — pin the line?";
  const noun = slots === 1 ? "song" : "songs";
  return `${slots} ${noun} left — pin the line?`;
});

const liveSetEndIndex = computed(() => {
  if (liveSlots.value == null) return -1;
  return resolveSetEndIndex({
    setEndSongCount: liveSlots.value,
    upNextSong: upNextSong.value,
    rankedSongs: sortedResultsFiltered.value,
    fallbackIndex: -1
  });
});

const setEndLineIndex = computed(() => {
  if (!isSetlistActive.value || !currentSetDuration.value) return -1;
  return resolveSetEndIndex({
    setEndSongCount: setEndSongCount.value,
    upNextSong: upNextSong.value,
    rankedSongs: sortedResultsFiltered.value,
    fallbackIndex: liveSetEndIndex.value
  });
});

// Zero songs left means the reserved Up Next is past the set too.
const setEndCoversUpNext = computed(
  () => Boolean(upNextSong.value) && effectiveSlots.value === 0 && setEndLineIndex.value === 0
);

const setEndLabel = computed(() => {
  if (effectiveStatus.value === "all-fit" || effectiveStatus.value === "inactive") return "Set end";
  const count = effectiveSlots.value;
  if (count == null || count === 0) return "Set end";
  const noun = count === 1 ? "song" : "songs";
  return `Set end — ${count} ${noun} fit`;
});

// Gates the "last song in set" confirm dialog when reserving Up Next.
const isNextSongLastInSet = computed(() => {
  if (!isSetlistActive.value || !currentSetDuration.value) return false;
  if (effectiveSlots.value == null) return false;
  return effectiveSlots.value <= 1;
});

const isPreviousSetExpired = computed(() => {
  const remaining = setRemainingSeconds.value;
  // -1 means the clock is not running, not that the set has finished.
  return !hasCurrentSong.value && isSetlistActive.value && remaining <= 0 && remaining !== -1;
});

const showStartSetButton = computed(() => {
  if (isKaraokeLive.value) return false;
  // A finished set is closed. Prepare Next Set is the next step, not Start set.
  if (isPreviousSetExpired.value) return false;
  if (!isPrevotingEvent.value) return true;
  // Pre-voting events: hide "Start set" / set-prep actions; keep "Play next song" if a song is active
  return hasCurrentSong.value;
});

// ── Set readiness indicators (for Start set button + prepared banner) ─────────
const preparedSetNumber = computed(() => (activeEvent.value?.currentSetIndex || 0) + 1);

const isSetPreparedAwaitingStart = computed(() => {
  const ev = activeEvent.value;
  if (!ev?.setlistEnabled || hasCurrentSong.value || ev.currentSetStartedAt) return false;
  return performedSongs.value.length > 0;
});

const startSetButtonLabel = computed(() => {
  if (hasCurrentSong.value) return "Play next song";
  if (isSetPreparedAwaitingStart.value) return "Start prepared set";
  return "Start set";
});

const startSetButtonIcon = computed(() => {
  if (hasCurrentSong.value) return "pi pi-forward";
  if (isSetPreparedAwaitingStart.value) return "pi pi-check-circle";
  return "pi pi-play";
});

const startSetButtonSeverity = computed(() => {
  if (hasCurrentSong.value) return "success";
  if (isSetPreparedAwaitingStart.value) return "info";
  if (isPreviousSetExpired.value) return "warn";
  return "success";
});

const startSetHelperText = computed(() => {
  if (hasCurrentSong.value) return null;
  if (isSetPreparedAwaitingStart.value) {
    const n = preparedSetNumber.value;
    if (currentSetDuration.value > 0) {
      return `Set ${n} is ready — first song will start the ${currentSetDuration.value} min timer`;
    }
    return `Set ${n} is ready — first song will start the set`;
  }
  if (isPreviousSetExpired.value) {
    return "Previous set ended — you'll be prompted to prepare the next set";
  }
  return null;
});

const handleVote = async (songId) => {
  if (!activeEvent.value) return;

  if (isKaraokeLive.value) {
    const song = songs.value.find((item) => item.id === songId);
    pendingKaraokeSong.value = song || { id: songId, title: "Unknown" };
    showKaraokePerformerModal.value = true;
    return;
  }

  const result = await votesStore.submitVote(activeEvent.value.id, songId);
  if (!result.success) {
    console.error("Vote failed:", result.message);
  }
};

const handleKaraokePerformerConfirm = async (performerName) => {
  if (!activeEvent.value || !pendingKaraokeSong.value) return;

  const songTitle = pendingKaraokeSong.value.title || "song";
  const songId = pendingKaraokeSong.value.id;

  const result = await votesStore.submitVote(activeEvent.value.id, songId, {
    performerName
  });

  pendingKaraokeSong.value = null;

  if (!result.success) {
    toast.add({
      severity: "error",
      summary: "Queue error",
      detail: result.message || "Failed to add song to queue",
      life: 4000
    });
    return;
  }

  toast.add({
    severity: "success",
    summary: "Added to queue",
    detail: `${performerName} queued for ${songTitle}`,
    life: 3000
  });

  await votesStore.fetchEventVotes(activeEvent.value.id);
};

// QR Scanner handlers
const handleQRAccepted = async (data) => {
  if (data.eventId !== activeEvent.value?.id) {
    toast.add({
      severity: "warn",
      summary: "Warning",
      detail: "QR code from different event - ignoring",
      life: 3000
    });
    return;
  }

  const songTitle = data.songTitle || "song";
  const submitResult = await votesStore.submitVote(data.eventId, data.songId, {
    performerName: data.performerName
  });

  if (!submitResult.success) {
    toast.add({
      severity: "error",
      summary: "Queue error",
      detail: submitResult.message || "Failed to add song to queue",
      life: 4000
    });
    return;
  }

  toast.add({
    severity: "success",
    summary: "Added to queue",
    detail: `${data.performerName} queued for ${songTitle}`,
    life: 4000
  });
};

const handleQRRejected = () => {
  // Called when admin rejects a scanned QR code signup
  console.log("QR signup rejected");
};

const handleQRModalClose = (value) => {
  showQrScanner.value = value;
};

const handleKaraokeStartSong = async (entry) => {
  if (!activeEvent.value || !entry) return;

  const result = await votesStore.markAsPerformed(activeEvent.value.id, entry.songId, {
    voteId: entry.voteId
  });

  if (!result.success) {
    toast.add({
      severity: "error",
      summary: "Start song failed",
      detail: result.message || "Could not start song",
      life: 4000
    });
    return;
  }

  await votesStore.fetchEventVotes(activeEvent.value.id);
};

const handleKaraokeMoveUp = (entry) => {
  moveKaraokeQueueEntry(entry, -1);
};

const handleKaraokeMoveDown = (entry) => {
  moveKaraokeQueueEntry(entry, 1);
};

const moveKaraokeQueueEntry = async (entry, offset) => {
  if (!activeEvent.value || !entry) return;

  const current = karaokeQueueEntries.value;
  const fromIndex = current.findIndex((item) => item.voteId === entry.voteId);
  const toIndex = fromIndex + offset;
  if (fromIndex === -1 || toIndex < 0 || toIndex >= current.length) return;

  const reordered = [...current];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, moved);

  const result = await votesStore.reorderKaraokeQueue(
    activeEvent.value.id,
    reordered.map((item) => item.voteId)
  );

  if (!result.success) {
    toast.add({
      severity: "error",
      summary: "Reorder failed",
      detail: result.message || "Could not reorder the karaoke queue",
      life: 4000
    });
  }
};

const handleKaraokeMarkPerformed = async (entry) => {
  if (!activeEvent.value || !entry) return;

  const result = await votesStore.setEndTime(activeEvent.value.id, entry.songId, {
    performedSongId: entry.performedSongId
  });

  if (!result.success) {
    toast.add({
      severity: "error",
      summary: "Mark performed failed",
      detail: result.message || "Could not end performance",
      life: 4000
    });
    return;
  }

  await votesStore.fetchEventVotes(activeEvent.value.id);
};

const handleMarkPerformed = async (songId) => {
  if (!activeEvent.value) return;

  // If there's a currently playing song, mark it as ended first
  if (currentlyPlayingSong.value) {
    const currentSongId = currentlyPlayingSong.value.id;
    const endResult = await votesStore.setEndTime(activeEvent.value.id, currentSongId);
    if (!endResult.success) {
      console.error("Failed to end current song:", endResult.message);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to end current song: " + (endResult.message || "Unknown error"),
        life: 5000
      });
      return;
    }
  }

  // Now mark the new song as performed
  const result = await votesStore.markAsPerformed(activeEvent.value.id, songId);
  if (result.success) {
    // Refetch votes to update the performed status
    await votesStore.fetchEventVotes(activeEvent.value.id);
    toast.add({
      severity: "success",
      summary: "Success",
      detail:
        "Song marked as performed" + (currentlyPlayingSong.value ? " and previous song ended" : ""),
      life: 3000
    });
  } else {
    console.error("Mark performed failed:", result.message);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: result.message || "Failed to mark song as performed",
      life: 5000
    });
  }
};

const applyClosedSet = (data) => {
  if (!data?.setExpired || !activeEvent.value) return;
  const count = Object.prototype.hasOwnProperty.call(data, "setEndSongCount")
    ? data.setEndSongCount
    : 0;
  activeEvent.value = {
    ...activeEvent.value,
    currentSetStartedAt: data.currentSetStartedAt,
    currentSetIndex: data.currentSetIndex ?? activeEvent.value.currentSetIndex,
    setEndSongCount: count
  };
  hydrateSetEndSongCount(count);
  // The clock is already expired on the server. Show that immediately;
  // the running interval keeps it at zero from the backdated start.
  setRemainingSeconds.value = 0;
};

// A refresh of a set that was ended early still has the old start time until
// this closes it. Play next never lands here: that path always has a current song.
const closeSetLeftOpen = async () => {
  if (isKaraokeLive.value) return;
  const event = activeEvent.value;
  const last = performedSongs.value[0];
  if (
    !event ||
    !setLeftOpenAfterSongEnded({
      setlistEnabled: event.setlistEnabled,
      setStartedAt: event.currentSetStartedAt,
      setDurationMinutes: currentSetDuration.value,
      hasCurrentSong: hasCurrentSong.value,
      lastSongEnded: Boolean(last?.endedAt),
      setEndSongCount: event.setEndSongCount
    })
  ) {
    return;
  }
  const result = await votesStore.setEndTime(event.id, last.id, { endSet: true });
  if (result.success) {
    applyClosedSet(result.data);
  }
};

const handleSetEnd = async (songId) => {
  if (!activeEvent.value) return;
  const result = await votesStore.setEndTime(activeEvent.value.id, songId, { endSet: true });
  if (result.success) {
    await votesStore.fetchEventVotes(activeEvent.value.id);
    applyClosedSet(result.data);
    toast.add({
      severity: "success",
      summary: "Set ended",
      detail: "The song is ended and this set is closed.",
      life: 3000
    });
  } else {
    console.error("Set end failed:", result.message);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: result.message || "Failed to set song end time",
      life: 5000
    });
  }
};

const handleClearUpNext = async () => {
  if (!activeEvent.value) return;
  clearingUpNext.value = true;
  try {
    const result = await votesStore.clearUpNext(activeEvent.value.id);
    if (result.success) {
      await votesStore.fetchEventVotes(activeEvent.value.id);
      toast.add({
        severity: "success",
        summary: "Unlocked",
        detail: "Song has been released back to the voting board",
        life: 3000
      });
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: result.message || "Failed to unlock song",
        life: 5000
      });
    }
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to unlock song",
      life: 5000
    });
  } finally {
    clearingUpNext.value = false;
  }
};

const showPlayNextError = (error) => {
  console.error("Play next song failed:", error);
  toast.add({
    severity: "error",
    summary: "Error",
    detail: error.response?.data?.message || error.message || "Failed to play next song",
    life: 5000
  });
};

const handlePlayNextSong = async () => {
  if (!activeEvent.value || playingNextSong.value) return;

  // Guard: previous set timer has expired and no song is playing — prompt before
  // starting the next set. Does not fire when a set is already prepared (no active timer).
  // This fires when the button reads "Start set" (!hasCurrentSong).
  if (
    activeEvent.value.setlistEnabled &&
    Array.isArray(activeEvent.value.setlistDurations) &&
    activeEvent.value.setlistDurations.length > 0 &&
    isPreviousSetExpired.value
  ) {
    confirm.require({
      message:
        "The current set has ended. Would you like to prepare the next set first, or start the song without a set timer?",
      header: "Set Not Prepared",
      icon: "pi pi-calendar",
      rejectClass: "p-button-secondary p-button-outlined",
      rejectLabel: "Start Without Set",
      acceptClass: "p-button-info",
      acceptLabel: "Set Up Next Set",
      accept: () => {
        handleStartNextSet({ autoPlay: true });
      },
      reject: () => {
        // Proceed directly without configuring a set
        executePlayNextSong();
      }
    });
    return;
  }

  // Safety guard: if the set is active, autoQueueNext is on, and the upcoming
  // reserved song would be the last to fit the set, ask the admin whether to
  // play with or without reserving the next "Up Next" song.
  if (
    isSetlistActive.value &&
    activeEvent.value.autoQueueNext &&
    isNextSongLastInSet.value &&
    hasCurrentSong.value
  ) {
    confirm.require({
      message:
        'The next song will be the last one fitting this set. Do you want to reserve another "Up Next" song after it, or play without reserving?',
      header: "Last Song in Set",
      icon: "pi pi-exclamation-triangle",
      rejectClass: "p-button-success",
      rejectLabel: "Play & Reserve Next",
      acceptClass: "p-button-warning",
      acceptLabel: "Play Without Reserving",
      accept: () => {
        // Play without reserving (no queue)
        handlePlayNextSongNoQueue();
      },
      reject: () => {
        // Normal flow - play and reserve
        executePlayNextSong();
      }
    });
    return;
  }

  await executePlayNextSong();
};

const executePlayNextSong = async () => {
  if (!activeEvent.value || playingNextSong.value) return;

  try {
    playingNextSong.value = true;

    // Step 1: If there's a currently being performed song, end it first
    const eventVotes = votesStore.getEventVotes(activeEvent.value.id);
    const currentSong = eventVotes.find((vote) => {
      return vote.isPerformed && vote.performedAt && !vote.endedAt;
    });

    if (currentSong) {
      const currentSongId = currentSong.songId || (currentSong.song && currentSong.song.id);
      if (!currentSongId) {
        showPlayNextError(new Error("Could not determine current song ID"));
        return;
      }
      const endResult = await votesStore.setEndTime(activeEvent.value.id, currentSongId);
      if (!endResult.success) {
        showPlayNextError(new Error(endResult.message || "Failed to end current song"));
        return;
      }
      await votesStore.fetchEventVotes(activeEvent.value.id);
    }

    // Step 2: Use the locked "up next" song if available, otherwise find highest-voted
    let nextSong = null;

    if (upNextSong.value) {
      nextSong = upNextSong.value;
    } else {
      const songsWithVotes = songs.value.map((song) => ({
        ...song,
        voteCount: getVoteCount(song.id),
        isPerformed: getIsPerformed(song.id),
        firstVotedAt: getFirstVotedAt(song.id)
      }));

      const availableSongs = songsWithVotes
        .filter((song) => !song.isPerformed && song.voteCount > 0)
        .sort((a, b) => {
          if (b.voteCount !== a.voteCount) return b.voteCount - a.voteCount;
          const aFirstVote = a.firstVotedAt ? new Date(a.firstVotedAt).getTime() : Infinity;
          const bFirstVote = b.firstVotedAt ? new Date(b.firstVotedAt).getTime() : Infinity;
          return aFirstVote - bFirstVote;
        });

      nextSong = availableSongs.length > 0 ? availableSongs[0] : null;
    }

    if (!nextSong) {
      toast.add({
        severity: "warn",
        summary: "No songs available",
        detail: "There are no songs with votes that haven't been performed yet",
        life: 5000
      });
      return;
    }

    // Step 3: Mark the song as performed
    const performResult = await votesStore.markAsPerformed(activeEvent.value.id, nextSong.id);

    if (performResult.success) {
      await votesStore.fetchEventVotes(activeEvent.value.id);
      toast.add({
        severity: "success",
        summary: "Success",
        detail: `"${nextSong.title}" is now being performed`,
        life: 3000
      });
    } else {
      showPlayNextError(new Error(performResult.message || "Failed to mark song as performed"));
    }
  } catch (error) {
    showPlayNextError(error);
  } finally {
    playingNextSong.value = false;
  }
};

// Handler for playing next song without auto-queueing (for set endings)
const handlePlayNextSongNoQueue = async () => {
  if (!activeEvent.value || playingNextSongNoQueue.value) return;

  try {
    playingNextSongNoQueue.value = true;

    // Step 1: If there's a currently being performed song, end it first
    const eventVotes = votesStore.getEventVotes(activeEvent.value.id);
    const currentSong = eventVotes.find((vote) => {
      return vote.isPerformed && vote.performedAt && !vote.endedAt;
    });

    if (currentSong) {
      const currentSongId = currentSong.songId || (currentSong.song && currentSong.song.id);
      if (!currentSongId) {
        showPlayNextError(new Error("Could not determine current song ID"));
        return;
      }
      const endResult = await votesStore.setEndTime(activeEvent.value.id, currentSongId);
      if (!endResult.success) {
        showPlayNextError(new Error(endResult.message || "Failed to end current song"));
        return;
      }
      await votesStore.fetchEventVotes(activeEvent.value.id);
    }

    // Step 2: Use the locked "up next" song if available, otherwise find highest-voted
    let nextSong = null;

    if (upNextSong.value) {
      nextSong = upNextSong.value;
    } else {
      const songsWithVotes = songs.value.map((song) => ({
        ...song,
        voteCount: getVoteCount(song.id),
        isPerformed: getIsPerformed(song.id),
        firstVotedAt: getFirstVotedAt(song.id)
      }));

      const availableSongs = songsWithVotes
        .filter((song) => !song.isPerformed && song.voteCount > 0)
        .sort((a, b) => {
          if (b.voteCount !== a.voteCount) return b.voteCount - a.voteCount;
          const aFirstVote = a.firstVotedAt ? new Date(a.firstVotedAt).getTime() : Infinity;
          const bFirstVote = b.firstVotedAt ? new Date(b.firstVotedAt).getTime() : Infinity;
          return aFirstVote - bFirstVote;
        });

      nextSong = availableSongs.length > 0 ? availableSongs[0] : null;
    }

    if (!nextSong) {
      toast.add({
        severity: "warn",
        summary: "No songs available",
        detail: "There are no songs with votes that haven't been performed yet",
        life: 5000
      });
      return;
    }

    // Step 3: Mark the song as performed WITH skipAutoQueue flag
    const performResult = await votesStore.markAsPerformed(activeEvent.value.id, nextSong.id, {
      skipAutoQueue: true
    });

    if (performResult.success) {
      await votesStore.fetchEventVotes(activeEvent.value.id);
      toast.add({
        severity: "success",
        summary: "Last Song",
        detail: `"${nextSong.title}" is now being performed (no next song queued)`,
        life: 3000
      });
    } else {
      showPlayNextError(new Error(performResult.message || "Failed to mark song as performed"));
    }
  } catch (error) {
    showPlayNextError(error);
  } finally {
    playingNextSongNoQueue.value = false;
  }
};

const fetchEventForRoute = async () => {
  let keepLoadingForRedirect = false;
  try {
    loading.value = true;
    eventLoadError.value = null;
    liveEventsPicker.value = [];

    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No authentication token found");
      return;
    }

    const response = await api.get("/api/events");
    if (response.data.success) {
      const events = response.data.data.events || [];
      const now = new Date();
      const live = getLiveEvents(events, now);
      const routeEventId = route.params.eventId;

      if (!routeEventId) {
        if (live.length === 0) {
          activeEvent.value = null;
        } else if (live.length === 1) {
          // Stay on the spinner until the route watcher finishes loading the event.
          keepLoadingForRedirect = true;
          await router.replace(`/events/live/${live[0].id}`);
          return;
        } else {
          activeEvent.value = null;
          liveEventsPicker.value = live;
        }
      } else {
        const event = events.find((e) => String(e.id) === String(routeEventId));

        if (!event || !isEventLive(event, now)) {
          if (activeEvent.value && joinedEventId) {
            socketStore.leaveEvent(joinedEventId);
            joinedEventId = null;
          }
          activeEvent.value = null;
          songs.value = [];
          eventLoadError.value = event ? "This event is not currently live." : "Event not found.";
        } else {
          if (joinedEventId && joinedEventId !== event.id) {
            socketStore.leaveEvent(joinedEventId);
            joinedEventId = null;
          }

          activeEvent.value = event;

          try {
            const songsResponse = await api.get(`/api/songs`);
            if (songsResponse.data.success) {
              const fetchedSongs = songsResponse.data.data.songs || [];
              songs.value = filterSongsForEvent(
                fetchedSongs.filter((song) => !song.isDisabled),
                activeEvent.value
              );
            }
          } catch (error) {
            console.error("Failed to fetch songs:", error);
          }

          await votesStore.fetchEventVotes(activeEvent.value.id);
          await votesStore.fetchUserVotes(activeEvent.value.id);
          await closeSetLeftOpen();

          if (!socketStore.isConnected) {
            socketStore.connect();
          }
          socketStore.joinEvent(activeEvent.value.id);
          joinedEventId = activeEvent.value.id;
        }
      }

      const liveIds = new Set(live.map((e) => e.id));
      const upcomingEvents = events
        .filter((event) => !event.isDisabled && event.eventStartTime && !liveIds.has(event.id))
        .map((event) => {
          let eventDate = null;
          if (event.eventStartTime) {
            eventDate = new Date(event.eventStartTime);
          }
          return { ...event, eventDate };
        })
        .filter((event) => event.eventDate && event.eventDate >= now)
        .sort((a, b) => a.eventDate - b.eventDate);

      nextEvent.value = upcomingEvents.length > 0 ? upcomingEvents[0] : null;
    }
  } catch (error) {
    console.error("Failed to fetch events:", error);
  } finally {
    if (!keepLoadingForRedirect) {
      loading.value = false;
    }
  }
};

// Format event start time
const formatEventStartTime = (event) => {
  if (event.eventStartTime) {
    return formatDateWithWeekday(event.eventStartTime);
  }
  return "";
};

const toggleVoting = async () => {
  if (!activeEvent.value || togglingVoting.value) return;

  try {
    togglingVoting.value = true;
    const newVotingDisabledStatus = !activeEvent.value.isVotingDisabled;

    const response = await api.put(`/api/events/${activeEvent.value.id}`, {
      ...activeEvent.value,
      isVotingDisabled: newVotingDisabledStatus
    });

    if (response.data.success) {
      // Update local state
      activeEvent.value.isVotingDisabled = newVotingDisabledStatus;

      toast.add({
        severity: "success",
        summary: "Success",
        detail: newVotingDisabledStatus ? "Voting has been stopped" : "Voting has been resumed",
        life: 3000
      });
    } else {
      console.error("Failed to toggle voting:", response.data.message);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: response.data.message || "Failed to update voting status",
        life: 5000
      });
    }
  } catch (error) {
    console.error("Failed to toggle voting:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: error.response?.data?.message || "Failed to update voting status",
      life: 5000
    });
  } finally {
    togglingVoting.value = false;
  }
};

const fetchOverlayTemplates = async () => {
  try {
    const response = await api.get("/api/overlay-templates");
    if (response.data.success) {
      overlayTemplates.value = response.data.data.overlayTemplates || [];
    }
  } catch (err) {
    console.error("Failed to load overlay templates:", err);
  }
};

const applyOverlayFromPayload = (template) => {
  if (!activeEvent.value) return;
  const templateId = template?.id ?? null;
  activeEvent.value.activeOverlayTemplateId = templateId;
  noteLiveEventOverlay(activeEvent.value.id, templateId);
  if (template?.id) {
    selectedOverlayId.value = template.id;
  }
};

const showPhoneOverlay = async () => {
  if (!activeEvent.value || !selectedOverlayId.value || overlaySaving.value) return;
  overlaySaving.value = true;
  try {
    const response = await postEventOverlay(api, activeEvent.value.id, selectedOverlayId.value);
    if (response.data.success) {
      applyOverlayFromPayload(response.data.data.overlayTemplate);
      toast.add({
        severity: "success",
        summary: "Overlay shown",
        detail: "The overlay is now visible on participant phones.",
        life: 4000
      });
    }
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: err.response?.data?.message || "Failed to show overlay",
      life: 5000
    });
  } finally {
    overlaySaving.value = false;
  }
};

const hidePhoneOverlay = async () => {
  if (!activeEvent.value || overlaySaving.value) return;
  overlaySaving.value = true;
  try {
    const response = await postEventOverlay(api, activeEvent.value.id, null);
    if (response.data.success) {
      applyOverlayFromPayload(null);
      toast.add({
        severity: "success",
        summary: "Overlay hidden",
        detail: "Participant phones are back on the voting screen.",
        life: 4000
      });
    }
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: err.response?.data?.message || "Failed to hide overlay",
      life: 5000
    });
  } finally {
    overlaySaving.value = false;
  }
};

const handleOverlayChanged = (data) => {
  if (!activeEvent.value || data.eventId !== activeEvent.value.id) return;
  applyOverlayFromPayload(data.overlayTemplate);
};

const confirmEndEvent = () => {
  if (!activeEvent.value) return;

  confirm.require({
    message:
      "Are you sure you want to end this event? This will stop all voting and close the event.",
    header: "End Event Confirmation",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    rejectLabel: "Cancel",
    acceptLabel: "End Event",
    accept: () => {
      endEvent();
    }
  });
};

const endEvent = async () => {
  if (!activeEvent.value || endingEvent.value) return;

  try {
    endingEvent.value = true;
    const now = new Date();

    const response = await api.put(`/api/events/${activeEvent.value.id}`, {
      ...activeEvent.value,
      isDisabled: true, // Stop all voting
      votingEndTime: now.toISOString() // Set voting end time to now
    });

    if (response.data.success) {
      // Update local state
      activeEvent.value.isDisabled = true;
      activeEvent.value.votingEndTime = now.toISOString();

      toast.add({
        severity: "success",
        summary: "Event Ended",
        detail: "The event has been ended. All voting has been stopped.",
        life: 5000
      });

      // Refresh the page to show the updated state
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } else {
      console.error("Failed to end event:", response.data.message);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: response.data.message || "Failed to end event",
        life: 5000
      });
    }
  } catch (error) {
    console.error("Failed to end event:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: error.response?.data?.message || "Failed to end event",
      life: 5000
    });
  } finally {
    endingEvent.value = false;
  }
};

// ── Setlist timer management ────────────────────────────────────────────────
const tickSetTimer = () => {
  if (!isSetlistActive.value || !currentSetDuration.value) {
    setRemainingSeconds.value = -1;
    return;
  }
  setRemainingSeconds.value = computeRemainingSetTime(
    currentSetDuration.value,
    activeEvent.value.currentSetStartedAt
  );
};

const startSetTimer = () => {
  stopSetTimer();
  tickSetTimer();
  setTimerInterval = setInterval(tickSetTimer, 1000);
};

const stopSetTimer = () => {
  if (setTimerInterval) {
    clearInterval(setTimerInterval);
    setTimerInterval = null;
  }
};

const fetchSetlistSettings = async () => {
  try {
    const response = await api.get("/api/settings/setlist/public");
    if (response.data.success) {
      setlistSettings.value = response.data.data;
    }
  } catch (err) {
    console.warn("Failed to fetch setlist settings:", err.message);
  }
};

// Handle set changes from socket
const handleSetChanged = (data) => {
  if (activeEvent.value && data.eventId === activeEvent.value.id) {
    activeEvent.value = {
      ...activeEvent.value,
      currentSetIndex: data.currentSetIndex,
      currentSetStartedAt: data.currentSetStartedAt,
      ...(data.setlistDurations !== undefined ? { setlistDurations: data.setlistDurations } : {}),
      ...(data.setEndSongCount !== undefined ? { setEndSongCount: data.setEndSongCount } : {})
    };
    if (data.setEndSongCount !== undefined) {
      hydrateSetEndSongCount(data.setEndSongCount);
    }
    tickSetTimer();
  }
  votesStore.handleSetChanged(data);
};

// True when clicking "Start Next Set" would go beyond the predefined set list
const isStartingExtraSet = computed(() => {
  const ev = activeEvent.value;
  if (!ev) return false;
  const durations = Array.isArray(ev.setlistDurations) ? ev.setlistDurations : [];
  return (ev.currentSetIndex || 0) + 1 >= durations.length;
});

// Handle "Prepare Next Set" button (prepare only — no auto-play)
const handleStartNextSet = async ({ autoPlay = false } = {}) => {
  if (!activeEvent.value) return;

  pendingSetAutoPlay.value = autoPlay;

  // If the next set would exceed the predefined list, ask the admin for a duration
  if (isStartingExtraSet.value) {
    extraSetDurationMin.value = null;
    showExtraSetModal.value = true;
    return;
  }

  const shouldAutoPlay = pendingSetAutoPlay.value;
  pendingSetAutoPlay.value = false;
  await executeStartNextSet(undefined, shouldAutoPlay);
};

const handleExtraSetModalCancel = () => {
  pendingSetAutoPlay.value = false;
  showExtraSetModal.value = false;
};

// Called after the modal is confirmed (with or without a duration)
const handleExtraSetConfirm = async (withDuration) => {
  const shouldAutoPlay = pendingSetAutoPlay.value;
  pendingSetAutoPlay.value = false;
  showExtraSetModal.value = false;
  const duration = withDuration && extraSetDurationMin.value > 0 ? extraSetDurationMin.value : 0;
  await executeStartNextSet(duration, shouldAutoPlay);
};

const executeStartNextSet = async (extraDuration = undefined, shouldAutoPlay = false) => {
  const result = await votesStore.startNextSet(activeEvent.value.id, extraDuration);
  if (result.success) {
    await fetchEventForRoute();
    hydrateSetEndSongCount(activeEvent.value?.setEndSongCount ?? null);
    if (shouldAutoPlay) {
      const setNumber = (activeEvent.value?.currentSetIndex || 0) + 1;
      toast.add({
        severity: "success",
        summary: "Next Set",
        detail: `Set ${setNumber} prepared — starting first song`,
        life: 3000
      });
      await executePlayNextSong();
    } else {
      toast.add({
        severity: "success",
        summary: "Next Set",
        detail: "Next set prepared",
        life: 3000
      });
    }
  } else {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: result.message || "Failed to start next set",
      life: 5000
    });
  }
};

const applyPersistedSetEndCount = (persisted) => {
  hydrateSetEndSongCount(persisted);
  if (activeEvent.value) {
    activeEvent.value = {
      ...activeEvent.value,
      setEndSongCount: persisted
    };
  }
};

const persistSetEndSongCount = async (count) => {
  if (!activeEvent.value || extendingSet.value) return;
  extendingSet.value = true;
  try {
    const result = await votesStore.setSetEndSongCount(activeEvent.value.id, count);
    if (!result.success) {
      throw new Error(result.message || "Failed to update set end");
    }
    const persisted =
      result.data && Object.prototype.hasOwnProperty.call(result.data, "setEndSongCount")
        ? result.data.setEndSongCount
        : count;
    applyPersistedSetEndCount(persisted);
    const detail =
      persisted == null
        ? "Set end line is following the timer"
        : `Set end marker set to ${persisted} song${persisted === 1 ? "" : "s"}`;
    toast.add({
      severity: "success",
      summary: "Set end updated",
      detail,
      life: 2500
    });
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: err.message || "Failed to update set end",
      life: 5000
    });
  } finally {
    extendingSet.value = false;
  }
};

// +/- pins the line. While unpinned the delta is applied to the live count.
const adjustSetEndSongCount = (delta) => {
  const base = setEndSongCount.value != null ? setEndSongCount.value : (liveSlots.value ?? 0);
  return persistSetEndSongCount(Math.max(0, base + delta));
};

const pinSetEndLine = () => persistSetEndSongCount(Math.max(0, liveSlots.value ?? 0));

const unpinSetEndLine = () => persistSetEndSongCount(null);

// Wrapper for song_performed that also updates set tracking data on the active event
const handleSongPerformedWithSetUpdate = async (data) => {
  // Update set tracking on the active event
  if (activeEvent.value && data.eventId === activeEvent.value.id) {
    const wasSetInactive = !activeEvent.value.currentSetStartedAt;
    activeEvent.value = {
      ...activeEvent.value,
      ...(data.currentSetStartedAt !== undefined
        ? {
            currentSetStartedAt: data.currentSetStartedAt,
            currentSetIndex: data.currentSetIndex ?? activeEvent.value.currentSetIndex
          }
        : {}),
      ...(data.setEndSongCount !== undefined ? { setEndSongCount: data.setEndSongCount } : {})
    };
    if (data.setEndSongCount !== undefined) {
      hydrateSetEndSongCount(data.setEndSongCount);
    } else if (setEndSongCount.value != null) {
      consumeSetEndSlot();
    }
    // Resume the set countdown after Set End stopped it (new set just started).
    if (wasSetInactive && data.currentSetStartedAt) {
      startSetTimer();
    }
  }
  // Delegate to the store handler for votes/upNext
  await votesStore.handleSongPerformed(data);
};

watch(
  () => route.params.eventId,
  async () => {
    stopSetTimer();
    setRemainingSeconds.value = -1;
    setEndSongCount.value = null;
    await fetchEventForRoute();
    hydrateSetEndSongCount(activeEvent.value?.setEndSongCount ?? null);
    startSetTimer();
  }
);

// Ensure Results tab always shows fresh data even if a socket update was missed
watch(activeView, async (newView) => {
  if (newView === "results" && activeEvent.value) {
    await votesStore.fetchEventVotes(activeEvent.value.id);
  }
});

onMounted(async () => {
  if (authStore.isAuthenticated && localStorage.getItem("token")) {
    // Ensure socket is connected first
    if (!socketStore.isConnected) {
      socketStore.connect();
    }

    // Register socket listeners AFTER socket is initialized
    socketStore.on("vote_updated", votesStore.handleVoteUpdated);
    socketStore.on("song_performed", handleSongPerformedWithSetUpdate);
    socketStore.on("song_ended", votesStore.handleSongEnded);
    socketStore.on("set_changed", handleSetChanged);
    socketStore.on("overlay_changed", handleOverlayChanged);

    await fetchSetlistSettings();
    await fetchOverlayTemplates();
    await fetchEventForRoute();
    if (activeEvent.value?.activeOverlayTemplateId) {
      selectedOverlayId.value = activeEvent.value.activeOverlayTemplateId;
    }
    hydrateSetEndSongCount(activeEvent.value?.setEndSongCount ?? null);

    // Start the set countdown and the live set-end line.
    startSetTimer();
    startSetEndSlots();
  } else {
    loading.value = false;
  }
});

onUnmounted(() => {
  stopSetTimer();
  stopSetEndSlots();
  socketStore.off("vote_updated", votesStore.handleVoteUpdated);
  socketStore.off("song_performed", handleSongPerformedWithSetUpdate);
  socketStore.off("song_ended", votesStore.handleSongEnded);
  socketStore.off("set_changed", handleSetChanged);
  socketStore.off("overlay_changed", handleOverlayChanged);

  if (joinedEventId) {
    socketStore.leaveEvent(joinedEventId);
    votesStore.cancelPendingRefresh(joinedEventId);
    joinedEventId = null;
  }
});
</script>

<style scoped>
.live-page-bottom-spacer {
  /* Flat baseline clears typical mobile browser toolbars (~50-80px), which
     env(safe-area-inset-bottom) alone does not account for (that only covers
     device notches / home indicators, not browser chrome). */
  height: calc(4rem + env(safe-area-inset-bottom, 0px));
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .live-page-bottom-spacer {
    display: none;
  }
}

.karaoke-queue-item {
  transition:
    transform 0.4s ease,
    opacity 0.3s ease;
}

.karaoke-queue-move {
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.karaoke-queue-enter-active,
.karaoke-queue-leave-active {
  transition:
    opacity 0.3s ease,
    max-height 0.35s ease;
  overflow: hidden;
}

.karaoke-queue-enter-from,
.karaoke-queue-leave-to {
  opacity: 0;
  max-height: 0;
}

/* Results card grid layout */
.results-card-layout {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas:
    "rank info votes"
    "button button button";
  gap: 0.5rem 1rem;
  align-items: center;
}

.results-card-rank {
  grid-area: rank;
}
.results-card-info {
  grid-area: info;
}
.results-card-votes {
  grid-area: votes;
}
.results-card-button {
  grid-area: button;
}

@media (min-width: 500px) {
  .results-card-layout {
    grid-template-columns: auto minmax(0, 1fr) auto auto;
    grid-template-areas: "rank info votes button";
  }
}
</style>
